import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Paper, Stack, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { DataTable, EmptyState, LoadingState } from './AdminPageUI';
import StudentAvatar from './StudentAvatar';
import api from '../utils/Api';

export default function GraduatedStudents({ onViewStudent }) {
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/enrollments/graduates');
      const groups = data.years || [];
      setYears(groups);
      setSelectedYear(previous => groups.some(group => group.year === previous) ? previous : groups[0]?.year || null);
      setPage(0);
    } catch (err) {
      setError('Unable to load graduated students. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const selectedGroup = years.find(group => group.year === selectedYear);
  const query = search.trim().toLowerCase();
  const students = (selectedGroup?.students || []).filter(student =>
    [student.name, student.admissionNumber, student.guardianPhone, student.guardianPhone2]
      .some(value => String(value || '').toLowerCase().includes(query)));
  const total = years.reduce((count, group) => count + group.count, 0);

  return <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, minWidth: 0, borderRadius: 3 }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2} mb={2}>
      <Box>
        <Typography variant="h6">Graduated Students</Typography>
        <Typography variant="body2" color="text.secondary">{total} students grouped by graduation year. Select a year to view its students.</Typography>
      </Box>
      <Button startIcon={<Refresh />} onClick={load} disabled={loading} sx={{ alignSelf: 'flex-start' }}>Refresh</Button>
    </Stack>
    {loading ? <LoadingState label="Loading graduated students…" /> : error ?
      <Alert severity="error" action={<Button color="inherit" onClick={load}>Retry</Button>}>{error}</Alert> : !years.length ?
        <EmptyState title="No graduated students yet" description="Students will appear here after they are marked as graduated during promotion." /> : <>
          <Box role="group" aria-label="Graduation years" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {years.map(group => <Chip key={group.year}
              label={`${group.year === 'Unknown' ? 'Year not recorded' : `Class of ${group.year}`} · ${group.count}`}
              color={selectedYear === group.year ? 'primary' : 'default'}
              variant={selectedYear === group.year ? 'filled' : 'outlined'}
              aria-pressed={selectedYear === group.year}
              onClick={() => { setSelectedYear(group.year); setPage(0); }} />)}
          </Box>
          {selectedGroup?.students.some(student => student.graduationYearInferred) &&
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>For older records without a saved graduation year, the year is inferred from the stored promotion academic year.</Typography>}
          <TextField label="Search graduates" placeholder="Name, admission number or contact" size="small" fullWidth
            value={search} onChange={event => { setSearch(event.target.value); setPage(0); }} sx={{ mb: 2, maxWidth: 480 }} />
          {!students.length ? <EmptyState title="No matching graduates" description="Try a different name, admission number or contact." /> : <>
            <DataTable>
              <Table size="small" aria-label="Graduated students">
                <TableHead><TableRow><TableCell>Student</TableCell><TableCell>Admission number</TableCell><TableCell>Guardian contact</TableCell><TableCell align="right">Details</TableCell></TableRow></TableHead>
                <TableBody>{students.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map(student =>
                  <TableRow key={student._id} hover>
                    <TableCell><Stack direction="row" gap={1.5} alignItems="center"><StudentAvatar student={student} sx={{ width: 36, height: 36 }} /><Typography variant="body2" sx={{ overflowWrap: 'anywhere', minWidth: 100 }}>{student.name}</Typography></Stack></TableCell>
                    <TableCell>{student.admissionNumber || '—'}</TableCell>
                    <TableCell>{[student.guardianPhone, student.guardianPhone2].filter(Boolean).join(' / ') || '—'}</TableCell>
                    <TableCell align="right"><Button size="small" onClick={() => onViewStudent(student)} aria-label={`View ${student.name}`}>View</Button></TableCell>
                  </TableRow>)}</TableBody>
              </Table>
            </DataTable>
            <TablePagination component="div" count={students.length} page={page} rowsPerPage={rowsPerPage}
              onPageChange={(_, value) => setPage(value)} rowsPerPageOptions={[10, 25, 50]}
              onRowsPerPageChange={event => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
              sx={{ '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', justifyContent: 'flex-end', px: 0 } }} />
          </>}
        </>}
  </Paper>;
}
